"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SyncExciseLabelsResponse = exports.ExciseLabelsState = exports.ExciseLabelRecord = exports.ExciseLabelReplicaRow = exports.EXCISE_LABELS_PAGE_MAX = exports.EXCISE_LABELS_PAGE_DEFAULT = exports.EXCISE_LABEL_STATUS_RESERVED = exports.EXCISE_LABEL_STATUS_SOLD = exports.EXCISE_LABEL_STATUS_READY = exports.EXCISE_LABELS_ROUTE = void 0;
const zod_1 = require("zod");
const sync_js_1 = require("./sync.js");
/**
 * The store's excise-label replica (VL-285): what the store system knows about
 * every excise stamp of the store — sold, ready for sale, or reserved by an
 * online order — served to its tills so a stamp already sold in the store is
 * refused at the scan (board 33), without a round trip per scan.
 *
 * The rows are the store system's own label table (ip-1 `t_excise_labels`),
 * imported like every other ip-1 table; the till keeps a local copy and asks
 * only that copy. That is the offline rule the store asked for: while the
 * Link (or Back Office) cannot be reached, the copy the till holds still
 * answers.
 *
 * ## Delta, with a periodic full walk
 *
 * Unlike the store catalogue this is NOT a whole-snapshot replacement: a
 * label's status changes with every sale in the store, and re-sending the
 * table on every change would be most of the traffic. A page is a run of rows
 * in `(updatedAt, id)` order after the cursor, which is a delta.
 *
 * A delta cannot express a row that was deleted (the store system purges old
 * sold labels), so the walk starts again from the beginning — a **sweep** —
 * on a cursor-less pull, when the source's imported generation changed, and
 * when the sweep the cursor belongs to is older than the server's re-walk
 * interval. Every page of a sweep names it in `sweep`; the last one says
 * `sweepComplete`, and the till then drops every row the sweep did not send.
 * Rows from a delta page are kept until the next sweep proves them gone.
 *
 * ## What a till does with a status
 *
 * `1` sold and `2` reserved by an online order are refused; `0` ready for
 * sale, any other value, and a label the replica does not hold are allowed
 * (decisions on VL-285, 2026-10-04). An unavailable feed or an empty replica
 * therefore allows every stamp — the store chose not to block a sale on a
 * replica it cannot trust to be complete.
 */
/** `GET /sync/excise-labels`. */
exports.EXCISE_LABELS_ROUTE = 'excise-labels';
/** The store system's statuses the till acts on. Others are carried as-is. */
exports.EXCISE_LABEL_STATUS_READY = 0;
exports.EXCISE_LABEL_STATUS_SOLD = 1;
exports.EXCISE_LABEL_STATUS_RESERVED = 2;
exports.EXCISE_LABELS_PAGE_DEFAULT = 1000;
exports.EXCISE_LABELS_PAGE_MAX = 5000;
/** One label as the store system holds it. */
exports.ExciseLabelReplicaRow = zod_1.z.object({
    /** The stamp code, verbatim (the store system's unique key). */
    label: zod_1.z.string().min(1).max(128),
    /**
     * The store system's status, verbatim. Null when the row holds none; only
     * `1` and `2` refuse a stamp, so a till allows such a label.
     */
    status: zod_1.z.number().int().nullable(),
    /** The register the label was sold on, when sold. */
    registerNumber: zod_1.z.number().int().nullable(),
    /** The receipt it was sold on: the store system's running receipt number. */
    receiptNumber: zod_1.z.number().int().nullable(),
    /** The store system's article id. */
    article: zod_1.z.string().nullable(),
    /** The product name the store system recorded with the label. */
    productName: zod_1.z.string().nullable(),
    /**
     * When the store system recorded it, as the import read it: an ISO-8601
     * instant (the store's zone-less timestamp read in the importing host's zone).
     */
    recordedAt: zod_1.z.string().nullable(),
});
exports.ExciseLabelRecord = zod_1.z.object({
    kind: zod_1.z.literal('excise-label'),
    /** The label — the record's identity on the till. */
    id: zod_1.z.string().min(1),
    payload: exports.ExciseLabelReplicaRow,
});
/**
 * `ready` serves rows. `unavailable` means the host has nothing it can vouch
 * for — no enabled source for the till's store, or no completed import — and
 * the till keeps the copy it holds.
 */
exports.ExciseLabelsState = zod_1.z.enum(['ready', 'unavailable']);
exports.SyncExciseLabelsResponse = zod_1.z.object({
    /** BOS terminal.locationId / verified Link claims.loc, never source or shop id. */
    locationId: zod_1.z.string().min(1).nullable(),
    state: exports.ExciseLabelsState,
    items: zod_1.z.array(exports.ExciseLabelRecord),
    cursor: sync_js_1.SyncCursor,
    hasMore: zod_1.z.boolean(),
    /** The sweep this page belongs to, or null for a delta page. */
    sweep: zod_1.z.string().min(1).nullable(),
    /** The last page of [sweep]: rows the sweep did not send are gone. */
    sweepComplete: zod_1.z.boolean(),
    snapshotAt: zod_1.z.string().datetime(),
});
//# sourceMappingURL=excise-labels.js.map